import authService from './auth.service';
import { Customer, CustomerDetailsResponse, CustomerListItemV2, CustomerPageOut, ResolvedCustomer } from '../types/customer.types';

const API_URL = '/api/v1';

export interface CustomerUpsertPayload {
  ruby_client_id?: string;
  identification_no?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  full_name?: string;
  private_first_name?: string;
  private_last_name?: string;
  gender?: string; // e.g. 'male' | 'female'
  date_of_birth?: string; // YYYY-MM-DD
  company_name?: string;
  position?: string;
  department?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  region_declared?: string;
  address_declared_city?: string;
  customer_type?: string; // 'individual' | 'company'
  status?: string; // 'active' | ...
  notes?: string;
  tags?: string[];
  custom_metadata?: Record<string, any>;
  tax_id?: string;
  website?: string;
  industry?: string;
  annual_revenue?: string;
  employee_count?: string;
  lead_source?: string;
  referral_source?: string;
  data_processing_consent?: boolean;
  phones?: PhoneEntry[];
  emails?: EmailEntry[];
}

export interface EmailEntry {
  id?: number;
  email: string;
  email_type: string;
  is_primary: boolean;
  is_verified?: boolean;
  customer_id?: number;
}

export interface PhoneEntry {
  id?: number;
  phone_number: string;
  phone_type: string;
  is_primary: boolean;
  is_verified?: boolean;
  customer_id?: number;
}

export interface CustomerDetails {
  id: number;
  identification_no?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string; // ISO date string (YYYY-MM-DD)
  phone_number?: string;
  phone_type?: string;
  email?: string;
  email_type?: string;
  gender?: string;
}

export interface CustomerFullDetails extends CustomerDetails {
  emails?: EmailEntry[];
  phones?: PhoneEntry[];
  tags?: string[];
}

export interface RefOption {
  value: string;
  label: string;
  id?: number;
}

class CustomerService {
  async getCustomers(params: { skip?: number; limit?: number; search?: string; filters?: Record<string, string | number | boolean | null | undefined> } = {}): Promise<CustomerPageOut> {
    const { skip = 0, limit = 100, search, filters } = params;

    try {
      const q = new URLSearchParams();
      q.set('skip', String(skip));
      q.set('limit', String(limit));
      if (search && search.trim()) q.set('search', search.trim());
      // Пробрасываем произвольные фильтры
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return;
        q.set(k, String(v));
      });
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/?${q.toString()}`
      );

      if (!response.ok) {
        throw new Error('Ошибка загрузки списка клиентов');
      }

      const data = await response.json();
      // Новый бэкенд возвращает { items, total, page, page_size }
      if (data && typeof data === 'object' && Array.isArray((data as any).items)) {
        return data as CustomerPageOut;
      }
      // Fallback: если прилетел массив — оборачиваем вручную
      if (Array.isArray(data)) {
        const items = data as CustomerListItemV2[];
        const page = Math.floor(skip / limit) + 1;
        return { items, total: items.length, page, page_size: limit };
      }
      // Иначе вернем пустую страницу
      return { items: [], total: 0, page: 1, page_size: limit };
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  async getCustomer(id: number, includeDeleted = false): Promise<CustomerFullDetails> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/${id}?include_deleted=${includeDeleted ? 'true' : 'false'}`
      );

      if (!response.ok) {
        throw new Error('Ошибка загрузки данных клиента');
      }

      const data = await response.json();

      // Попробуем выделить первичный email/телефон из массивов, если они есть
      let primaryEmailItem: any | undefined;
      if (Array.isArray(data.emails) && data.emails.length > 0) {
        primaryEmailItem = data.emails.find((e: any) => e.is_primary) || data.emails[0];
      }

      let primaryPhoneItem: any | undefined;
      if (Array.isArray(data.phones) && data.phones.length > 0) {
        primaryPhoneItem = data.phones.find((p: any) => p.is_primary) || data.phones[0];
      }

      // Нормализация возможных вариантов структуры ответа к ожидаемым полям
      const normalized: CustomerFullDetails = {
        id: data.id,
        identification_no: data.identification_no ?? data.iin ?? data.inn ?? undefined,
        first_name: data.first_name ?? data.given_name ?? undefined,
        last_name: data.last_name ?? data.family_name ?? undefined,
        date_of_birth: data.date_of_birth ?? data.birth_date ?? undefined,
        gender: data.gender ?? undefined,
        // email может быть как строка, так и в массиве объектов
        email:
          data.email ??
          data.primary_email ??
          (primaryEmailItem?.email || primaryEmailItem?.address) ??
          undefined,
        email_type:
          data.email_type ??
          (primaryEmailItem?.email_type || primaryEmailItem?.type || primaryEmailItem?.label) ??
          undefined,
        // phone может быть как строка, так и в массиве объектов
        phone_number:
          data.phone_number ??
          data.primary_phone ??
          data.phone ??
          (primaryPhoneItem?.phone_number || primaryPhoneItem?.number) ??
          undefined,
        phone_type:
          data.phone_type ??
          (primaryPhoneItem?.phone_type || primaryPhoneItem?.type || primaryPhoneItem?.label) ??
          undefined,
        // Полные массивы для редактирования
        emails: Array.isArray(data.emails)
          ? data.emails.map((e: any) => ({
              id: e.id,
              email: e.email ?? e.address ?? '',
              email_type: e.email_type ?? e.type ?? e.label ?? 'personal',
              is_primary: !!e.is_primary,
              is_verified: e.is_verified,
              customer_id: e.customer_id,
            }))
          : (data.email || data.primary_email)
          ? [
              {
                email: data.email || data.primary_email,
                email_type: data.email_type || 'personal',
                is_primary: true,
              },
            ]
          : [],
        phones: Array.isArray(data.phones)
          ? data.phones.map((p: any) => ({
              id: p.id,
              phone_number: p.phone_number ?? p.number ?? '',
              phone_type: p.phone_type ?? p.type ?? p.label ?? 'mobile',
              is_primary: !!p.is_primary,
              is_verified: p.is_verified,
              customer_id: p.customer_id,
            }))
          : (data.phone_number || data.primary_phone || data.phone)
          ? [
              {
                phone_number: data.phone_number || data.primary_phone || data.phone,
                phone_type: data.phone_type || 'mobile',
                is_primary: true,
              },
            ]
          : [],
        tags: Array.isArray(data.tags) ? data.tags.map((t: any) => String(t)) : [],
      };

      return normalized;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      throw error;
    }
  }

  // Полный ответ без нормализации — для страницы карточки клиента
  async getCustomerRaw(id: number, includeDeleted = false): Promise<CustomerDetailsResponse> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/customers/${id}?include_deleted=${includeDeleted ? 'true' : 'false'}`
    );
    if (!response.ok) {
      throw new Error('Ошибка загрузки данных клиента');
    }
    const raw = await response.json();
    // Новый бэкенд может отдавать массив c единственным объектом
    const obj = Array.isArray(raw) ? (raw[0] ?? null) : raw;
    if (!obj) throw new Error('Клиент не найден');
    return obj as CustomerDetailsResponse;
  }

  async getReference(code: 'gender' | 'phone_type' | 'email_type'): Promise<RefOption[]> {
    try {
      // Новый эндпоинт для справочника пола
      if (code === 'gender') {
        const res = await authService.fetchWithAuth(`${API_URL}/dicts/genders/`);
        if (!res.ok) throw new Error('Не удалось загрузить справочник пола');
        const items = await res.json();
        if (!Array.isArray(items)) return [];
        return items.map((it: any) => ({
          value: String(it.code ?? it.id ?? ''),
          label: String(it.name ?? it.code ?? ''),
          id: typeof it.id === 'number' ? it.id : undefined,
        }));
      }

      // Новый эндпоинт для типов телефонов
      if (code === 'phone_type') {
        const res = await authService.fetchWithAuth(`${API_URL}/dicts/phone_types/`);
        if (!res.ok) throw new Error('Не удалось загрузить справочник типов телефонов');
        const items = await res.json();
        if (!Array.isArray(items)) return [];
        return items.map((it: any) => ({
          value: String(it.code ?? it.id ?? ''),
          label: String(it.name ?? it.code ?? ''),
          id: typeof it.id === 'number' ? it.id : undefined,
        }));
      }

      // Новый эндпоинт для типов email
      if (code === 'email_type') {
        const res = await authService.fetchWithAuth(`${API_URL}/dicts/email_types/`);
        if (!res.ok) throw new Error('Не удалось загрузить справочник типов email');
        const items = await res.json();
        if (!Array.isArray(items)) return [];
        return items.map((it: any) => ({
          value: String(it.code ?? it.id ?? ''),
          label: String(it.name ?? it.code ?? ''),
          id: typeof it.id === 'number' ? it.id : undefined,
        }));
      }

      // Старый маршрут удалён. Для других кодов вернём пустой список.
      return [];
    } catch (e) {
      console.error('Error fetching reference', code, e);
      return [];
    }
  }

  async createCustomer(data: CustomerUpsertPayload): Promise<Customer> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/`,
        {
          method: 'POST',
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Ошибка при создании клиента';

        // Обработка различных типов ошибок
        if (response.status === 409) {
          errorMessage = 'Клиент с таким email уже существует';
        } else if (response.status === 400) {
          const detail = errorData?.detail;
          if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            // FastAPI validation errors
            errorMessage = detail.map((err: any) => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else if (detail && typeof detail === 'object') {
            errorMessage = JSON.stringify(detail);
          } else {
            errorMessage = 'Некорректные данные';
          }
        } else if (errorData?.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  async updateCustomer(
    id: number,
    data: CustomerUpsertPayload
  ): Promise<Customer> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Ошибка при обновлении клиента';
        
        // Обработка различных типов ошибок
        if (response.status === 409) {
          errorMessage = 'Клиент с таким email уже существует';
        } else if (response.status === 400) {
          errorMessage = errorData?.detail || 'Некорректные данные';
        } else if (response.status === 404) {
          errorMessage = 'Клиент не найден';
        } else if (errorData?.detail) {
          errorMessage = errorData.detail;
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  async updateCustomerPartial(
    id: number,
    data: Partial<{
      identification_no: string;
      full_name: string;
      private_first_name: string;
      private_last_name: string;
      gender_id: number;
      gender_code: string;
      date_of_birth: string;
      region_declared: string;
      address_declared_city: string;
      phones: Array<{ type_code: string; number: string }>;
      emails: Array<{ type_code: string; email: string }>;
    }>
  ): Promise<Customer> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Ошибка при обновлении клиента';
        if (response.status === 400) errorMessage = errorData?.detail || errorMessage;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating customer (partial):', error);
      throw error;
    }
  }

  /**
   * Получить клиента по ID (для секции клиента в договоре)
   * @param customerId ID клиента
   */
  async getCustomerById(customerId: number): Promise<ResolvedCustomer> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/${customerId}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Не удалось загрузить данные клиента';
        if (response.status === 404) {
          errorMessage = 'Клиент не найден';
        } else if (errorData?.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Нормализуем данные к формату ResolvedCustomer
      const normalized: ResolvedCustomer = {
        id: data.id,
        ruby_client_id: data.ruby_client_id ?? null,
        identification_no: data.identification_no ?? null,
        full_name: data.full_name ?? null,
        private_first_name: data.private_first_name ?? data.first_name ?? null,
        private_last_name: data.private_last_name ?? data.last_name ?? null,
        private_middle_name: data.private_middle_name ?? data.middle_name ?? null,
        gender_code: data.gender_code ?? data.gender ?? null,
        date_of_birth: data.date_of_birth ?? null,
        region_declared: data.region_declared ?? null,
        address_declared_city: data.address_declared_city ?? data.city ?? null,
        client_id: data.client_id ?? null,
        locale: data.locale ?? null,
        phones: Array.isArray(data.phones)
          ? data.phones.map((p: any) => ({
              id: p.id,
              type_code: p.type_code ?? p.phone_type ?? 'mobile',
              number: p.number ?? p.phone_number ?? '',
            }))
          : [],
        emails: Array.isArray(data.emails)
          ? data.emails.map((e: any) => ({
              id: e.id,
              type_code: e.type_code ?? e.email_type ?? 'personal',
              status_code: e.status_code ?? null,
              email: e.email ?? '',
            }))
          : [],
      };

      return normalized;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw error;
    }
  }

  /**
   * Получить список телефонов клиента
   * GET /api/v1/customers/{id}/phones
   * @param customerId ID клиента
   */
  async getCustomerPhones(customerId: number): Promise<Array<{ id: number; type_code: string; number: string }>> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/${customerId}/phones`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Не удалось загрузить телефоны клиента';
        if (response.status === 404) {
          errorMessage = 'Клиент не найден';
        } else if (errorData?.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Нормализуем данные
      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((p: any) => ({
        id: p.id,
        type_code: p.type_code ?? p.phone_type ?? 'mobile',
        number: p.number ?? p.phone_number ?? '',
      }));
    } catch (error) {
      console.error('Error fetching customer phones:', error);
      throw error;
    }
  }

  /**
   * Получить клиента по связанной сущности
   * @param entityType Тип сущности (e.g. "agreements", "tickets", "svc_custom")
   * @param entityId ID сущности
   */
  async resolveCustomer(entityType: number | string, entityId: number): Promise<ResolvedCustomer> {
    try {
      const response = await authService.fetchWithAuth(
        `${API_URL}/customers/resolve?entity_type=${entityType}&entity_id=${entityId}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = 'Не удалось найти клиента';
        if (response.status === 404) {
          errorMessage = 'Клиент не найден для данной сущности';
        } else if (errorData?.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resolving customer:', error);
      throw error;
    }
  }
}

export default new CustomerService();

export type UpdateCustomerPayload = CustomerUpsertPayload;
